import React, { useState, useEffect, useRef } from "react";
import {
  X,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Clock,
  Users,
  Plus,
  Minus,
  Calculator,
} from "lucide-react";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { cn } from "../../../lib/utils";
import { scaleRecipe } from "wasp/client/operations";
import {
  formatQty,
  normalizeIngredientDisplay,
  canonicalizeIngredientName,
} from "../../../shared/culinary";

interface RecipeStep {
  id: string;
  instruction: string;
  duration?: number; // minutes
  temperature?: string;
  ingredients?: string[];
}

interface RecipeIngredientView {
  name: string;
  quantity?: number;
  unit?: string;
  notes?: string;
}

interface Recipe {
  id: string;
  title: string;
  servings: number;
  prepTime: number;
  cookTime: number;
  steps: RecipeStep[];
  ingredients: RecipeIngredientView[];
}

interface CookingInterfaceProps {
  recipe: Recipe;
  onClose: () => void;
  onComplete?: () => void;
}

export function CookingInterface({
  recipe,
  onClose,
  onComplete,
}: CookingInterfaceProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [timer, setTimer] = useState<number | null>(null);
  const [timerActive, setTimerActive] = useState(false);
  const [targetServings, setTargetServings] = useState(recipe.servings);
  const [scaledIngredients, setScaledIngredients] = useState<any[]>([]);
  const [scaledTotalMinutes, setScaledTotalMinutes] = useState<number | null>(null);
  const [scaling, setScaling] = useState(false);
  const [showIngredients, setShowIngredients] = useState(false);

  const totalSteps = recipe.steps.length;
  const progress = (completedSteps.size / totalSteps) * 100;
  const scaleFactor = targetServings / recipe.servings;
  const isScaled = scaleFactor !== 1;

  // Scale recipe when servings change
  const lastScaleKeyRef = useRef<string>("");

  useEffect(() => {
    if (isScaled && recipe.id) {
      const key = `${recipe.id}:${targetServings}`;
      if (lastScaleKeyRef.current === key) return;
      lastScaleKeyRef.current = key;
      setScaling(true);
      scaleRecipe({
        recipeId: recipe.id,
        targetServings: targetServings,
        saveAsUserRecipe: false,
      })
        .then((result: any) => {
          console.log('[CookingInterface] Scale result:', {
            hasResult: !!result,
            hasScaledRecipe: !!result?.scaledRecipe,
            ingredientsCount: result?.scaledRecipe?.ingredients?.length,
            firstIngredient: result?.scaledRecipe?.ingredients?.[0],
            scaledTotalMinutes: result?.scaledRecipe?.totalMinutes
          });
          if (result?.scaledRecipe?.ingredients) {
            setScaledIngredients(result.scaledRecipe.ingredients);
          }
          if (result?.scaledRecipe?.totalMinutes !== undefined) {
            setScaledTotalMinutes(result.scaledRecipe.totalMinutes);
          }
        })
        .catch((error) => {
          console.error("Failed to scale recipe:", error);
          // Reset to original ingredients on error
          setScaledIngredients([]);
        })
        .finally(() => {
          setScaling(false);
        });
    } else {
      setScaledIngredients([]);
      setScaledTotalMinutes(null);
      lastScaleKeyRef.current = "";
    }
  }, [targetServings, recipe.id, recipe.servings, isScaled]);

  // Timer functionality
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timerActive && timer !== null && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev === null || prev <= 1) {
            setTimerActive(false);
            // Timer finished - could add notification here
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerActive, timer]);

  const startTimer = (minutes: number) => {
    setTimer(minutes * 60); // Convert to seconds
    setTimerActive(true);
  };

  const stopTimer = () => {
    setTimerActive(false);
    setTimer(null);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleStepComplete = () => {
    const newCompleted = new Set(completedSteps);
    newCompleted.add(currentStep);
    setCompletedSteps(newCompleted);

    // Auto-advance to next step if not on last step
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else if (newCompleted.size === totalSteps) {
      // All steps completed
      onComplete?.();
    }
  };

  const currentStepData = recipe.steps[currentStep];

  return (
    <div className="flex flex-col bg-white rounded-lg shadow-cardShadow">
      {/* Header */}
      <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white p-4">
        <div className="flex items-center space-x-3">
          <Button variant="ghost" size="sm" onClick={onClose} className="p-2">
            <X className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="truncate font-header text-lg font-semibold text-gray-900">
                {recipe.title}
              </h1>
              <span className="rounded-md bg-blue-100 px-2 py-0.5 text-xs text-blue-700 dark:text-blue-300">
                Recipe Scaler · Beta
              </span>
            </div>
            <div className="flex items-center space-x-3 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setTargetServings(Math.max(1, targetServings - 1))
                  }
                  disabled={targetServings <= 1 || scaling}
                  className="h-6 w-6 p-0"
                >
                  <Minus className="h-3 w-3" />
                </Button>
                <span className="flex items-center font-medium">
                  <Users className="mr-1 h-4 w-4" />
                  {targetServings}{" "}
                  {targetServings === 1 ? "serving" : "servings"}
                  {isScaled && (
                    <span className="ml-1 text-xs text-coral">
                      ({scaleFactor.toFixed(1)}x)
                    </span>
                  )}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setTargetServings(Math.min(20, targetServings + 1))
                  }
                  disabled={targetServings >= 20 || scaling}
                  className="h-6 w-6 p-0"
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Timer Display */}
        {timer !== null && (
          <div className="flex items-center space-x-2">
            <div
              className={cn(
                "flex items-center rounded-full px-3 py-1 text-sm font-medium",
                timerActive
                  ? "bg-orange-100 text-orange-800"
                  : "bg-gray-100 text-gray-600",
              )}
            >
              <Clock className="mr-1 h-4 w-4" />
              {formatTime(timer)}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={stopTimer}
              className="text-gray-600"
            >
              Stop
            </Button>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="border-b border-gray-100 bg-gray-50 px-4 py-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            Step {currentStep + 1} of {totalSteps}
          </span>
          <span className="text-sm text-gray-600">
            {completedSteps.size} completed
          </span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="space-y-6 p-6">
          {/* Ingredients Section - Always Visible */}
          <div
            className={cn(
              "rounded-lg border p-4",
              isScaled
                ? "bg-sage/10 border-sage/20"
                : "border-gray-200 bg-gray-50",
            )}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calculator
                  className={cn(
                    "h-4 w-4",
                    isScaled ? "text-sage" : "text-gray-600",
                  )}
                />
                <span
                  className={cn(
                    "text-sm font-medium",
                    isScaled ? "text-sage" : "text-gray-700",
                  )}
                >
                  {isScaled
                    ? `Recipe scaled to ${targetServings} servings (${scaleFactor.toFixed(1)}x)`
                    : `Recipe ingredients (${targetServings} servings)`}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowIngredients(!showIngredients)}
                className={cn(
                  isScaled
                    ? "text-sage hover:bg-sage/10"
                    : "text-gray-700 hover:bg-gray-100",
                )}
              >
                {showIngredients ? "Hide" : "Show"} Ingredients
              </Button>
            </div>

            {/* Scaling Info Box */}
            {isScaled && scaledIngredients.length > 0 && (
              <div className="mt-3 rounded-lg bg-sage/10 border border-sage/20 p-2.5 flex items-center justify-between text-sm">
                <span className="font-medium text-sage">
                  Scaled {scaleFactor.toFixed(1)}x
                </span>
                {scaledTotalMinutes && (
                  <span className="text-sage flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Est. {scaledTotalMinutes} min
                  </span>
                )}
              </div>
            )}

            {showIngredients && (
              <div className="mt-4 space-y-2">
                <h4 className="text-sm font-medium text-gray-700">
                  {isScaled ? "Scaled Ingredients:" : "Ingredients:"}
                </h4>
                <div className="grid gap-2">
                  {(isScaled && scaledIngredients.length > 0
                    ? scaledIngredients
                    : // If recipe.ingredients are strings, map to objects; else use as-is
                      (recipe.ingredients as any[]).map((ing: any) =>
                        typeof ing === "string"
                          ? {
                              name: ing,
                              quantity: undefined,
                              unit: undefined,
                              notes: undefined,
                            }
                          : ing,
                      )
                  ).map((ingredient: any, index: number) => (
                    <div
                      key={index}
                      className={cn(
                        "rounded p-2 text-sm",
                        isScaled
                          ? "bg-white"
                          : "border border-gray-100 bg-white",
                      )}
                    >
                      <span>
                        {(() => {
                          const qty = ingredient.quantity;
                          const unit = ingredient.unit || "";
                          const displayQty =
                            qty !== undefined && qty !== null
                              ? formatQty(qty, unit)
                              : "";
                          const disp = normalizeIngredientDisplay(
                            ingredient.name ||
                              ingredient.ingredientName ||
                              (ingredient.ingredient &&
                                ingredient.ingredient.name) ||
                              ingredient.ingredientId ||
                              "Ingredient",
                          );
                          const name = canonicalizeIngredientName(disp.name);
                          return (
                            <>
                              {displayQty && (
                                <span
                                  className={cn(
                                    "font-medium",
                                    isScaled ? "text-sage" : "text-gray-900",
                                  )}
                                >
                                  {displayQty}{" "}
                                </span>
                              )}
                              <span className="text-gray-700">
                                {name}
                                {ingredient.modifier && (
                                  <span className="ml-1 text-sm font-medium text-sage">
                                    ({ingredient.modifier})
                                  </span>
                                )}
                                {(ingredient.notes || disp.notesExtra) && (
                                  <span className="ml-1 text-xs text-gray-500">
                                    ({ingredient.notes || disp.notesExtra})
                                  </span>
                                )}
                              </span>
                            </>
                          );
                        })()}
                      </span>
                    </div>
                  ))}
                </div>
                {isScaled && (
                  <p className="mt-2 text-xs text-gray-600">
                    💡 Tip: Adjust seasoning to taste when scaling recipes
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Side-by-side Original vs Scaled table */}
          {isScaled && scaledIngredients.length > 0 && (
            <div className="rounded-lg border border-gray-200 p-4">
              <h3 className="mb-2 text-sm font-medium text-gray-700">
                Scaled vs Original
              </h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="font-medium text-gray-600">Original</div>
                <div className="font-medium text-gray-600">Scaled</div>
                {scaledIngredients.map((s: any, idx: number) => {
                  const orig =
                    recipe.ingredients.find(
                      (r: any) =>
                        r.ingredientId && r.ingredientId === s.ingredientId,
                    ) || recipe.ingredients[idx];
                  const origQty =
                    orig && orig.quantity != null
                      ? formatQty(orig.quantity, orig.unit || "")
                      : "";
                  const scaledQty =
                    s && s.quantity != null
                      ? formatQty(s.quantity, s.unit || "")
                      : "";
                  const dispOrig = normalizeIngredientDisplay(orig?.name || "");
                  return (
                    <React.Fragment key={idx}>
                      <div className="text-gray-700">
                        {origQty && (
                          <span className="mr-1 font-medium">{origQty}</span>
                        )}
                        <span>{canonicalizeIngredientName(dispOrig.name)}</span>
                      </div>
                      <div className="text-gray-700">
                        {scaledQty && (
                          <span className="mr-1 font-medium">{scaledQty}</span>
                        )}
                        <span>
                          {canonicalizeIngredientName(
                            normalizeIngredientDisplay(s.name || "").name,
                          )}
                        </span>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}

          {/* Current Step */}
          <div className="rounded-lg border border-coral/20 bg-coral/5 p-6">
            <div className="mb-4 flex items-start justify-between">
              <h2 className="font-header text-lg font-semibold text-gray-900">
                Current Step
              </h2>
              {completedSteps.has(currentStep) && (
                <CheckCircle className="h-6 w-6 text-green-600" />
              )}
            </div>

            <p className="mb-4 text-lg leading-relaxed text-gray-800">
              {currentStepData.instruction}
            </p>

            {/* Step Duration */}
            {currentStepData.duration && (
              <div className="mb-4 flex items-center justify-between rounded-lg bg-white p-3">
                <span className="text-sm font-medium text-gray-700">
                  Estimated time: {currentStepData.duration} minutes
                </span>
                <Button
                  onClick={() => startTimer(currentStepData.duration!)}
                  size="sm"
                  variant="outline"
                  className="border-orange-300 text-orange-700 hover:bg-orange-50"
                >
                  <Clock className="mr-1 h-4 w-4" />
                  Start Timer
                </Button>
              </div>
            )}

            {/* Step Ingredients */}
            {currentStepData.ingredients &&
              currentStepData.ingredients.length > 0 && (
                <div className="rounded-lg bg-white p-3">
                  <h4 className="mb-2 text-sm font-medium text-gray-700">
                    Ingredients for this step:
                  </h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    {currentStepData.ingredients.map((ingredient, index) => (
                      <li key={index} className="flex items-center">
                        <span className="mr-2 h-2 w-2 flex-shrink-0 rounded-full bg-gray-400" />
                        {ingredient}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
          </div>

          {/* Upcoming Steps Preview */}
          {currentStep < totalSteps - 1 && (
            <div className="rounded-lg border border-gray-200 p-4">
              <h3 className="mb-2 text-sm font-medium text-gray-700">
                Next: Step {currentStep + 2}
              </h3>
              <p className="line-clamp-2 text-sm text-gray-600">
                {recipe.steps[currentStep + 1].instruction}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Footer */}
      <div className="border-t border-gray-200 bg-white p-4">
        <div className="flex items-center justify-between space-x-3">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
            className="flex items-center"
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Previous
          </Button>

          {!completedSteps.has(currentStep) ? (
            <Button
              onClick={handleStepComplete}
              className="flex-1 bg-coral text-white hover:bg-coral/90"
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Complete Step
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => {
                const newCompleted = new Set(completedSteps);
                newCompleted.delete(currentStep);
                setCompletedSteps(newCompleted);
              }}
              className="flex-1"
            >
              Mark Incomplete
            </Button>
          )}

          <Button
            variant="outline"
            onClick={() =>
              setCurrentStep(Math.min(totalSteps - 1, currentStep + 1))
            }
            disabled={currentStep === totalSteps - 1}
            className="flex items-center"
          >
            Next
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
